// src/app/app.config.ts
import type {
  ApplicationConfig} from '@angular/core';
import { APP_INITIALIZER, ErrorHandler, provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.routes';
import { ConfigService } from './core/config/config.service';
import { TranslationService } from './core/i18n/translation.service';
import { GlobalErrorHandler } from './core/logging/global-error-handler';

import { timeoutInterceptor }      from './core/http/timeout.interceptor';
import { authInterceptor }         from './core/http/auth.interceptor';
import { sessionCancelInterceptor } from './core/http/session-cancel.interceptor';
import { retryInterceptor }        from './core/http/retry.interceptor';
import { cacheInterceptor }        from './core/http/cache.interceptor';
import { errorInterceptor }        from './core/http/error.interceptor';

/**
 * Fábrica del APP_INITIALIZER.
 * Ejecuta dos promesas secuenciales:
 * 1. ConfigService.load()      — carga y valida runtime-config.json
 * 2. TranslationService.initialize() — carga el idioma inicial (sin pantalla sin texto)
 *
 * El bootstrap de Angular se bloquea hasta que ambas resuelven.
 * Si alguna falla: el error se muestra en #app-loading y el bootstrap se detiene.
 */
function initializeApp(
  config: ConfigService,
  translation: TranslationService
): () => Promise<void> {
  return async () => {
    // Paso 1: Cargar y validar la configuración de runtime
    await config.load();
    // Paso 2: Resolver idioma inicial y cargar el JSON de traducciones
    // Segundo paso secuencial — necesita config cargado para leer i18n settings
    await translation.initialize();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    // ── 1. Router con input binding ──────────────────────────────────────────
    // withComponentInputBinding: los parámetros de ruta se pasan como @Input()
    provideRouter(routes, withComponentInputBinding()),

    // ── 2. Zone.js con coalescing para mejor performance ─────────────────────
    provideZoneChangeDetection({ eventCoalescing: true }),

    // ── 3. HttpClient con interceptors en orden correcto ──────────────────────
    // Orden de aplicación (exterior → interior en el request):
    //   timeout → auth → sessionCancel → retry → cache → error
    // En la respuesta el orden se invierte (innermost primero).
    // sessionCancelInterceptor va antes de retryInterceptor a propósito
    // (Prompt 19, D9/§1.5): así el corte de tráfico al cerrar sesión también
    // alcanza a los reintentos que estén esperando su backoff.
    provideHttpClient(
      withInterceptors([
        timeoutInterceptor,        // Envuelve todo — deadline global
        authInterceptor,           // Adjunta headers de autenticación
        sessionCancelInterceptor,  // Corta el tráfico en curso al cerrar sesión (D9)
        retryInterceptor,          // Reintenta GETs fallidos
        cacheInterceptor,          // Sirve desde caché si disponible
        errorInterceptor,          // Maneja errores finales (innermost)
      ])
    ),

    // ── 4. APP_INITIALIZER — bloquea el bootstrap ─────────────────────────────
    // NUNCA omitir multi: true — permite múltiples APP_INITIALIZER en la misma app
    {
      provide:    APP_INITIALIZER,
      useFactory: initializeApp,
      deps:       [ConfigService, TranslationService],
      multi:      true,
    },

    // ── 5. ErrorHandler global ────────────────────────────────────────────────
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};
